'use client'

import { Modal, Textarea, TextInput } from "@mantine/core"
import { LoadingSpinner } from "../misc/loadingSpinner"
import ModalTemplate from "./templates/modalTemplate"
import { useModalStore } from "@/context/modalStore";
import { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import { Session } from "next-auth";
import { toast } from "sonner";
import { useStateStore } from "@/context/stateStore";
import { INote } from "@/models/types/note";
import { CreateNote } from "@/utils/noteHelpers/create";
import { ICycle } from "@/models/types/cycle";
import { IPlant } from "@/models/types/plant";
import { usePathname, useRouter } from "next/navigation";

export default function NewNoteModal({ session, handleUpdate, currPlant, currCycle }: { session: Session | null, handleUpdate: () => Promise<void>, currPlant: IPlant, currCycle: ICycle }) {

    const pathname = usePathname();
    const router = useRouter();
    const [loading, setLoading] = useState<boolean>(false);
    const openNewNoteModal = useModalStore(state => state.openNewNoteModal);
    const setOpenNewNoteModal = useModalStore(state => state.setOpenNewNoteModal);
    const setOverlay = useStateStore(state => state.setOverlay);
    const noteForm = useForm({
        mode: 'uncontrolled',
        initialValues: {
            note: {
                _id: '',
                noteName: '',
                note: '',
                relatedType: (currPlant !== {} as IPlant && currPlant !== undefined && currPlant !== null && currPlant._id) ? 'plant' : (currCycle !== {} as ICycle && currCycle !== undefined && currCycle !== null && currCycle._id) ? 'cycle' : 'general',
                relatedId: '',
                relatedName: '',
                userId: '',
                dateMade: new Date(),
                lastEdited: new Date(),
            } as INote,
        },
        validate: {
            note: {
                note: (value) => (value.trim().length < 1 ? 'Note cannot be empty' : null),
            }
        }
    });

    const resetZoom = useStateStore(state => state.handleZoomReset);
    const width = useStateStore(state => state.widthQuery);

    const handleCancel = () => {
        noteForm.reset();
        noteForm.clearErrors();
        setOverlay(false);
        router.refresh();
        resetZoom(width, false);
        setOpenNewNoteModal(false);
        setLoading(false);
    }

    const HandleCreateNote = async () => {

        setLoading(true);

        try {

            noteForm.clearErrors();

            if (!session) {
                toast.warning("You must be signed in!");
                setLoading(false);
                return;
            }

            const sessionUser = session.user;

            if (!sessionUser) {
                toast.error('User session is invalid or missing email');
                setLoading(false);
                return;
            }

            const validation = noteForm.validate();

            if (validation.hasErrors) {
                noteForm.setErrors(validation.errors);
                toast.error('Please fix the errors in the form');
                setLoading(false);
                return;
            }

            const noteCheck = noteForm.getValues().note;

            if (!noteCheck || !noteCheck.note || noteCheck.note.trim() === '') {
                toast.error('Note content cannot be empty');
                setLoading(false);
                return;
            }

            const propsForNote = pathname.includes('plant') ? {
                relatedType: 'plant',
                relatedId: currPlant ? currPlant._id : '',
                relatedName: currPlant ? currPlant.name : '',
            } : pathname.includes('growing') ? {
                relatedType: 'cycle',
                relatedId: currCycle ? currCycle._id : '',
                relatedName: currCycle ? currCycle.name : '',
            } : pathname.includes('notes') ? {
                relatedType: 'general',
                relatedId: '--',
                relatedName: 'General note',
            } : {
                relatedType: 'unknown',
                relatedId: '',
                relatedName: '',
            };

            if (!propsForNote || (propsForNote && (propsForNote.relatedType === 'plant' || propsForNote.relatedType === 'cycle') && propsForNote.relatedId === '') || !propsForNote.relatedName) {
                toast.error('Current item is invalid');
                setLoading(false);
                return;
            }

            const noteName = noteCheck.noteName.trim();
            if (!noteName || noteName === '') {
                toast.error('Note name cannot be empty');
                setLoading(false);
                return;
            }

            const noteToPass = {
                _id: '',
                noteName: noteName,
                note: noteCheck.note.trim(),
                dateMade: new Date(),
                lastEdited: new Date(),
                relatedType: propsForNote.relatedType,
                relatedId: propsForNote.relatedId,
                relatedName: `${propsForNote.relatedName}`,
                userId: sessionUser.id,
            } as INote;

            const urlToUse = process.env.NEXT_PUBLIC_BASE_URL ? process.env.NEXT_PUBLIC_BASE_URL as string : '';

            let createNote = await CreateNote({ note: noteToPass, user: sessionUser.email, urlToUse: urlToUse }) as { status: boolean, message: string };

            let attemptStatus = createNote ? createNote.status : false;

            if (attemptStatus !== true) {
                toast.error('Error creating note attempt');
                setLoading(false);
                return;
            }
            toast.success('Note created successfully!');
            await handleUpdate();
            handleCancel();
        } catch (error) {
            setLoading(false);
            return;
        }
    }

    useEffect(() => {
        if (currPlant !== {} as IPlant && currPlant !== undefined && currPlant !== null && currPlant._id) {
            noteForm.setValues({
                note: {
                    ...noteForm.values.note,
                    relatedId: currPlant._id,
                    relatedName: currPlant.name,
                    relatedType: 'plant',
                }
            });
        } else if (currCycle !== {} as ICycle && currCycle !== undefined && currCycle !== null && currCycle._id) {
            noteForm.setValues({
                note: {
                    ...noteForm.values.note,
                    relatedId: currCycle._id,
                    relatedName: currCycle.name,
                    relatedType: 'cycle',
                }
            });
        } else {
            noteForm.setValues({
                note: {
                    ...noteForm.values.note,
                    relatedId: '-',
                    relatedName: 'General note',
                    relatedType: 'general',
                }
            });
        }
    }, [currPlant, currCycle]);

    const pClass = "text-[12px] sm:text-[14px] md:text-[15px] truncate";
    //const buttonClass = "border border-neutral-200 rounded-md p-2 w-1/5 sm:w-1/3 md:w-1/4 lg:w-1/5 truncate";

    return (
        <Modal
            opened={openNewNoteModal}
            onClose={handleCancel}
            title="New Note"
            centered
            size={'xl'}
            overlayProps={{
                backgroundOpacity: 0.55, blur: 3, className: 'drop-shadow-xl'
            }}
            lockScroll={true}
            styles={{
                body: { height: '70vh', display: 'flex', flexDirection: 'column' },
                content: { height: 'auto' }

            }}>
            <ModalTemplate subtitle={null}>
                {loading ? (
                    <LoadingSpinner />
                ) : (
                    <form id="modalNoteForm" onSubmit={noteForm.onSubmit(HandleCreateNote)} onAbort={handleCancel} className="w-full">
                        <div className="flex-1 min-h-0 overflow-auto scrollbar-thin scrollbar-webkit py-1 px-1 sm:py-2 sm:px-2 bg-slate-100/70 rounded-md shadow-[inset_0_2px_8px_rgba(0,0,0,0.10),inset_0_-2px_8px_rgba(0,0,0,0.10)] border border-gray-300">
                            <TextInput
                                placeholder="Note Name"
                                label="Name"
                                withAsterisk
                                mb={10}
                                key={noteForm.key('note.noteName')}
                                {...noteForm.getInputProps('note.noteName')}
                            />
                            <Textarea
                                placeholder="Notes for this plant/growing session..."
                                key={noteForm.key('note.note')}
                                autosize
                                minRows={15}
                                maxRows={15}
                                resize="vertical"
                                {...noteForm.getInputProps('note.note')}
                            />
                        </div>
                        <section className="flex-shrink-0 flex flex-row w-full justify-evenly items-center pt-3 bg-white border-t border-gray-200 mt-2">
                            <button type="button" onClick={handleCancel} className="cursor-pointer border border-neutral-200 rounded-md hover:bg-neutral-200 p-2 w-1/5 sm:w-1/3 md:w-1/4 lg:w-1/5">
                                <p className={`${pClass} text-gray-900`}>
                                    Cancel
                                </p>
                            </button>
                            <button type='submit' className="cursor-pointer border border-neutral-200 rounded-md hover:bg-blue-200 bg-blue-400 p-2 w-1/5 sm:w-1/3 md:w-1/4 lg:w-1/5">
                                Save
                            </button>
                        </section>
                    </form>
                )}
            </ModalTemplate>
        </Modal>
    )
}
